/**
 * Local event agenda: API types, the /events client and the agenda's URL state.
 *
 * Event dates are the event's own local calendar dates, never publication or
 * ingestion times. An unknown start time stays unknown (null); nothing here or
 * in the screens turns it into midnight.
 *
 * Agenda state lives in the URL with the API's own parameter names, so reload,
 * Back and a shared link reproduce the same range, filters and page. The details
 * parameter (`karte`) is never touched by the agenda.
 */
import { request, type CardView } from './api';
import { DETAIL_PARAM } from './components/detail-link';

// ---------------------------------------------------------------- types

export type EventPreset = 'today' | 'weekend' | 'upcoming';
export type EventPresetResolved = EventPreset | 'custom';
export type EventLocalityFilter = 'basel-region' | 'other' | 'all';
export type EventLocality = 'basel-region' | 'other' | 'unknown';
export type EventStatus = 'scheduled' | 'postponed' | 'rescheduled' | 'cancelled' | 'unknown';
export type TicketStatus = 'available' | 'sold_out' | 'unknown';
export type CoverageStatus = 'success' | 'empty' | 'partial' | 'failed' | 'never';

export interface EventConflict {
  field: string;
  this_value: string | null;
  other_value: string | null;
}

export interface RelatedEvent {
  id: string;
  source_id: string;
  source_name: string;
  title: string;
  venue: string | null;
  local_date: string;
  start_local: string | null;
  canonical_url: string;
  conflicts: EventConflict[];
}

/** The event facts shared by a live occurrence and a card version's payload. */
export interface EventFacts {
  source_id: string;
  source_name: string;
  source_event_id: string;
  occurrence_key: string;
  canonical_url: string;
  listing_url: string | null;
  title: string;
  subtitle: string | null;
  artists: string[];
  category: string | null;
  categories: string[];
  organizer: string | null;
  venue: string | null;
  venue_basis: string | null;
  city: string | null;
  locality: EventLocality;
  locality_basis: string | null;
  /** YYYY-MM-DD, the event's own date in its timezone. */
  local_date: string;
  /** "HH:MM", or null when the source gives no time (date-only precision). */
  start_local: string | null;
  end_local: string | null;
  /** The end may fall on the next day (overnight). */
  end_local_date: string | null;
  doors_local: string | null;
  start_at: string | null;
  end_at: string | null;
  doors_at: string | null;
  date_precision: 'date' | 'datetime';
  timezone: string;
  timezone_basis: string;
  description: string | null;
  language: string | null;
  ticket_url: string | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
  min_age: number | null;
  event_status: EventStatus;
  ticket_status: TicketStatus;
  /** Previous local date/time text, e.g. "2026-10-03 20:00". */
  rescheduled_from: string | null;
}

export interface EventOccurrence extends EventFacts {
  id: string;
  first_seen_at: string;
  last_checked_at: string;
  last_seen_in_listing_at: string | null;
  missing_since: string | null;
  related: RelatedEvent[];
  /** The occurrence's content card; version.id is what `?karte=` takes. */
  card: CardView | null;
}

/** Card version payload for payload_type 'event.v1'. */
export interface EventPayload extends EventFacts {
  payload_type: 'event.v1';
  /** When these facts were read (RFC3339). */
  observed_at: string;
  /** Language code → page URL of the same event in another language. */
  alternate_urls: Record<string, string>;
  evidence_urls: string[];
}

export interface EventFacet {
  id: string;
  name: string;
  count: number;
}

export interface EventCoverage {
  source_id: string;
  source_name: string;
  status: CoverageStatus;
  completed_at: string | null;
  warning_count: number;
  note: string | null;
}

export interface EventsResponse {
  results: EventOccurrence[];
  total: number;
  next_offset: number | null;
  as_of: string;
  /** Resolved local dates, inclusive. */
  from: string;
  to: string;
  preset: EventPresetResolved;
  locality: EventLocalityFilter;
  facets: { sources: EventFacet[]; categories: EventFacet[]; localities: Array<{ id: string; count: number }> };
  coverage: EventCoverage[];
}

export function isEventPayload(p: { payload_type?: string } | null | undefined): p is EventPayload {
  return !!p && p.payload_type === 'event.v1';
}

// ---------------------------------------------------------------- URL state

export const EVENTS_PAGE_SIZE = 30;
const PRESETS: EventPresetResolved[] = ['today', 'weekend', 'upcoming', 'custom'];
const LOCALITIES: EventLocalityFilter[] = ['basel-region', 'other', 'all'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export interface EventsUrlState {
  /** 'custom' means the from/to range applies. */
  preset: EventPresetResolved;
  from: string;
  to: string;
  source: string[];
  category: string[];
  locality: EventLocalityFilter;
  offset: number;
  /** Pins the result set while paging; only kept with offset > 0. */
  as_of: string;
}

export const DEFAULT_EVENTS_STATE: EventsUrlState = {
  preset: 'upcoming',
  from: '',
  to: '',
  source: [],
  category: [],
  locality: 'basel-region',
  offset: 0,
  as_of: '',
};

export function parseEventsState(params: URLSearchParams): EventsUrlState {
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const validFrom = DAY.test(from) ? from : '';
  const validTo = DAY.test(to) ? to : '';
  const rawPreset = params.get('preset') ?? '';
  let preset: EventPresetResolved;
  if ((PRESETS as string[]).includes(rawPreset)) preset = rawPreset as EventPresetResolved;
  else preset = validFrom || validTo ? 'custom' : 'upcoming';
  const locality = params.get('locality') ?? '';
  const offset = Number(params.get('offset') ?? '0');
  const cleanOffset = Number.isFinite(offset) && offset > 0 ? Math.floor(offset) : 0;
  return {
    preset,
    from: preset === 'custom' ? validFrom : '',
    to: preset === 'custom' ? validTo : '',
    source: params.getAll('source').filter(Boolean),
    category: params.getAll('category').filter(Boolean),
    locality: (LOCALITIES as string[]).includes(locality) ? (locality as EventLocalityFilter) : 'basel-region',
    offset: cleanOffset,
    as_of: cleanOffset > 0 ? params.get('as_of') ?? '' : '',
  };
}

/**
 * The URL for a state. Defaults are left out so the plain agenda is `/events`.
 * Pass the current parameters to carry an open details parameter over unchanged.
 */
export function eventsStateToSearch(s: EventsUrlState, current?: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams();
  if (s.preset === 'custom') {
    p.set('preset', 'custom');
    if (s.from) p.set('from', s.from);
    if (s.to) p.set('to', s.to);
  } else if (s.preset !== 'upcoming') {
    p.set('preset', s.preset);
  }
  for (const v of s.source) p.append('source', v);
  for (const v of s.category) p.append('category', v);
  if (s.locality !== 'basel-region') p.set('locality', s.locality);
  if (s.offset > 0) {
    p.set('offset', String(s.offset));
    if (s.as_of) p.set('as_of', s.as_of);
  }
  const karte = current?.get(DETAIL_PARAM);
  if (karte) p.set(DETAIL_PARAM, karte);
  return p;
}

/** Any change other than paging starts again at the first page with a fresh result set. */
export function withEventsChange(s: EventsUrlState, change: Partial<EventsUrlState>): EventsUrlState {
  const paging = 'offset' in change || 'as_of' in change;
  return paging ? { ...s, ...change } : { ...s, ...change, offset: 0, as_of: '' };
}

/** True when anything narrows the result beyond the date range and the default place. */
export function hasEventFilters(s: EventsUrlState): boolean {
  return s.source.length > 0 || s.category.length > 0 || s.locality !== 'basel-region';
}

/** The API query string for a state (no leading "?"). */
export function buildEventsQuery(s: EventsUrlState, limit = EVENTS_PAGE_SIZE): string {
  const q = new URLSearchParams();
  // The API's preset wins over from/to, so a custom range sends no preset.
  if (s.preset === 'custom') {
    if (s.from) q.set('from', s.from);
    if (s.to) q.set('to', s.to);
  } else {
    q.set('preset', s.preset);
  }
  for (const v of s.source) q.append('source', v);
  for (const v of s.category) q.append('category', v);
  q.set('locality', s.locality);
  q.set('limit', String(limit));
  if (s.offset > 0) {
    q.set('offset', String(s.offset));
    if (s.as_of) q.set('as_of', s.as_of);
  }
  return q.toString();
}

// ---------------------------------------------------------------- client

/* The Go API encodes an empty list as null; normalise once here. */
const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

function normaliseOccurrence(o: EventOccurrence): EventOccurrence {
  return {
    ...o,
    artists: list(o.artists),
    categories: list(o.categories),
    related: list(o.related).map((r) => ({ ...r, conflicts: list(r.conflicts) })),
    card: o.card ? { ...o.card, version: { ...o.card.version, evidence: list(o.card.version?.evidence) } } : null,
  };
}

/*
 * Occurrences the agenda has loaded, by their card version id. The details modal
 * is addressed by version id only; when it opens from the agenda, this gives it
 * the live record (last checked, listing presence, related sources) for exactly
 * that version. A direct link or an older version simply finds nothing.
 */
const byVersion = new Map<string, EventOccurrence>();
const BY_VERSION_MAX = 500;

export function occurrenceForVersion(versionId: string): EventOccurrence | undefined {
  return byVersion.get(versionId);
}

function remember(o: EventOccurrence) {
  const id = o.card?.version.id;
  if (!id) return;
  byVersion.delete(id);
  byVersion.set(id, o);
  if (byVersion.size > BY_VERSION_MAX) byVersion.delete(byVersion.keys().next().value!);
}

export async function fetchEvents(state: EventsUrlState, init?: RequestInit): Promise<EventsResponse> {
  const r = await request<EventsResponse>(`/events?${buildEventsQuery(state)}`, init);
  const results = list(r.results).map(normaliseOccurrence);
  results.forEach(remember);
  return {
    ...r,
    results,
    facets: {
      sources: list(r.facets?.sources),
      categories: list(r.facets?.categories),
      localities: list(r.facets?.localities),
    },
    coverage: list(r.coverage),
  };
}

/** Normalises an event payload read from a card version (lists may be null). */
export function normaliseEventPayload(p: EventPayload): EventPayload {
  return {
    ...p,
    artists: list(p.artists),
    categories: list(p.categories),
    evidence_urls: list(p.evidence_urls),
    alternate_urls: p.alternate_urls ?? {},
  };
}
